/**
 * WAVE 2A: Flight Mode Panel
 * 
 * Allows selection of flight mode (VLOS/EVLOS/BVLOS) with:
 * - Radio button selection
 * - Tooltips explaining each mode
 * - Real-time category impact display
 * - Bilingual support (CS/EN)
 */
import { useState, useEffect } from "react";
import { Eye, Users, Radio as RadioIcon, AlertCircle, CheckCircle } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

const FLIGHT_MODES = [
  {
    value: "VLOS",
    icon: Eye,
    badgeColor: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700",
    categoryImpact: "open",
  },
  {
    value: "EVLOS",
    icon: Users,
    badgeColor: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700",
    categoryImpact: "specific",
  },
  {
    value: "BVLOS",
    icon: RadioIcon,
    badgeColor: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700",
    categoryImpact: "specific",
  },
];

export const FlightModePanel = ({
  value = "VLOS",
  onChange,
  disabled = false,
  language = "cs",
}) => {
  // Translations
  const t = language === "cs" ? {
    sectionTitle: "Režim letu",
    sectionSubtitle: "Vyberte typ operace",
    vlos: "VLOS",
    vlosDescription: "Visual Line of Sight — přímý vizuální kontakt s dronem",
    evlos: "EVLOS",
    evlosDescription: "Extended VLOS — s řetězcem pozorovatelů",
    bvlos: "BVLOS",
    bvlosDescription: "Beyond VLOS — mimo přímou viditelnost",
    impactNote: "Režim letu ovlivňuje kategorii operace",
    openEligible: "Způsobilý pro Open kategorii",
    specificRequired: "Vyžaduje Specific kategorii",
  } : {
    sectionTitle: "Flight Mode",
    sectionSubtitle: "Select operation type",
    vlos: "VLOS",
    vlosDescription: "Visual Line of Sight — direct visual contact with drone",
    evlos: "EVLOS",
    evlosDescription: "Extended VLOS — with observer chain",
    bvlos: "BVLOS",
    bvlosDescription: "Beyond VLOS — beyond direct line of sight",
    impactNote: "Flight mode affects operational category",
    openEligible: "Open category eligible",
    specificRequired: "Specific category required",
  };

  const getDescription = (mode) => {
    if (mode === "VLOS") return t.vlosDescription;
    if (mode === "EVLOS") return t.evlosDescription;
    return t.bvlosDescription;
  };

  const getCategoryBadge = (mode) => {
    const config = FLIGHT_MODES.find((m) => m.value === mode);
    if (config?.categoryImpact === "open") {
      return (
        <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-300 dark:border-green-700">
          <CheckCircle className="h-3 w-3 mr-1" />
          {t.openEligible}
        </Badge>
      );
    }
    return (
      <Badge className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700">
        <AlertCircle className="h-3 w-3 mr-1" />
        {t.specificRequired}
      </Badge>
    );
  };

  return (
    <div className="space-y-4" data-testid="flight-mode-panel">
      <div>
        <h4 className="text-sm font-semibold text-[var(--text-primary)]">
          {t.sectionTitle}
        </h4>
        <p className="text-xs text-[var(--text-muted)] mt-0.5">
          {t.sectionSubtitle}
        </p>
      </div>

      <TooltipProvider>
        <RadioGroup
          value={value}
          onValueChange={onChange}
          disabled={disabled}
          className="grid grid-cols-3 gap-3"
          data-testid="flight-mode-radio-group"
        >
          {FLIGHT_MODES.map((mode) => {
            const Icon = mode.icon;
            const isSelected = value === mode.value;

            return (
              <Tooltip key={mode.value}>
                <TooltipTrigger asChild>
                  {/* CheckMate SAIL refresh unification (2026-09, Codex review): this used
                      to be a plain <div> with its own onClick(mode.value) AND wrap a
                      <Label htmlFor=...> pointing at the RadioGroupItem below — a single
                      click on the visible text forwarded via native label-for semantics to
                      the RadioGroupItem (Radix RadioGroup.onValueChange), then bubbled up
                      through this element's own onClick, firing onChange twice (three times
                      counting Radix's own internal click handling) for one user click. Fixed
                      by making the WHOLE card itself the <Label> (single native forwarding
                      path to the radio button) and removing the onClick entirely — the
                      RadioGroup's onValueChange is now the only source of mutation calls. */}
                  <Label
                    htmlFor={`flight-mode-${mode.value}`}
                    className={`relative flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    } ${
                      isSelected
                        ? `${mode.badgeColor} border-current`
                        : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--border-hover)]"
                    }`}
                    data-testid={`flight-mode-${mode.value.toLowerCase()}`}
                  >
                    <RadioGroupItem
                      value={mode.value}
                      id={`flight-mode-${mode.value}`}
                      className="absolute top-2 right-2"
                    />
                    <Icon className={`h-6 w-6 mb-2 ${isSelected ? "" : "text-[var(--text-muted)]"}`} />
                    <span className={`font-semibold ${isSelected ? "" : "text-[var(--text-secondary)]"}`}>
                      {mode.value}
                    </span>
                  </Label>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px]">
                  <p>{getDescription(mode.value)}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </RadioGroup>
      </TooltipProvider>

      {/* Category impact indicator */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-default)]">
        <span className="text-xs text-[var(--text-muted)]">{t.impactNote}</span>
        {getCategoryBadge(value)}
      </div>
    </div>
  );
};

export default FlightModePanel;
